/**
 * Help Center content — Part 3: Incidents, Risk Assessments, Hours of Rest.
 * Imported by helpContent/index.js; do not import directly.
 */

// ─── 6. INCIDENTS ────────────────────────────────────────────────────────────

export const incidents = {
  id: 'incidents',
  title: '6. Incidents',
  summary: 'Report, investigate and close out vessel incidents as part of your SMS.',
  keywords: ['incident', 'accident', 'near miss', 'hazard', 'maib', 'mca', 'harbourmaster', 'report', 'investigate', 'root cause', 'corrective action', 'close', 'history'],
  steps: [
    {
      text: 'The Incidents module lets you report, investigate and close out vessel incidents (accidents, near misses, hazards, environmental events, equipment failures, personnel injuries) as part of your SMS. The screen also shows a WBC3 Reporting Requirements reminder (MAIB serious casualties within 24h; MCA safety-critical incidents; Harbourmaster port incidents immediately; SMS records for all incidents). It has two tabs: Incidents (active) and Incident History (closed). Reach it from left menu → Incidents.',
      image: 'incident-02-landing.png',
    },
    {
      text: 'Click Report Incident. Set Type (Accident / Near Miss / Hazard-Unsafe Condition / Environmental / Equipment Failure / Personnel Injury), Date, Time and Location.',
      image: 'incident-03-report-form.png',
    },
    {
      text: 'Optionally set Daylight, Weather, Visibility, Sea State and Wind Force. Fill What Happened? (minimum 50 characters, factual only). Choose Reported By, tick any Persons Directly Involved / Witnesses, and tick any Injuries / Damage / Pollution flags and Severity Indicators that apply.',
      image: 'incident-04-filled.png',
    },
    {
      text: 'Read the Reporting Requirements Assessment (it tells you whether external MAIB/MCA/Harbourmaster notification is likely needed), then click Submit Report → OK. The incident shows as Open in the Active list.',
      image: 'incident-05-after-submit.png',
    },
    {
      text: 'Click the incident card to open its detail. Set Investigation Status and assign an Investigator, enter the Root Cause, tick relevant Contributing Factors, add Lessons Learned, and add one or more Corrective Actions (description + assignee + due date + Add Action).',
      image: 'incident-06-detail.png',
    },
    {
      text: 'Important: click Save Changes before you close the incident. If you fill in the Root Cause and go straight to Close in one pass, the unsaved Root Cause can be lost — so Save Changes first, then close.',
      image: 'incident-08-investigation-filled.png',
    },
    {
      text: 'The app blocks closing without a Root Cause ("Missing Information — Please enter a root cause before closing"). Once it\'s saved, tick Effectiveness Verified (ISM Code 9.2), then click Quick Close (Minor Incident) or Close Incident and Confirm on the dialog.',
      image: 'incident-11-close-dialog.png',
    },
    {
      text: 'The incident leaves the Active list and appears in Incident History, where you can filter, Export CSV, and click View Report to reopen the full record.',
      image: null,
    },
  ],
};

// ─── 7. RISK ASSESSMENTS ─────────────────────────────────────────────────────

export const riskAssessments = {
  id: 'risk-assessments',
  title: '7. Risk Assessments',
  summary: 'Identify hazards, score risk, record control measures, and produce a print-ready report.',
  keywords: ['risk', 'assessment', 'hazard', 'likelihood', 'severity', 'matrix', 'controls', 'residual', 'pdf', 'signature', 'wbc3', 'ism', 'solas'],
  steps: [
    {
      text: 'The Risk Assessments module identifies hazards, scores the risk, and records control measures for a task or operation. It is a compliance record (badges: WBC3 · ISM · SOLAS) built with a 5-step wizard: Task Details → Hazards → Risk Score → Controls → Review. Saved assessments become a print/PDF-ready report with a hazard register and signature block. Reach it from left menu → Risk Assessments.',
      image: 'risk-01-landing.png',
    },
    {
      text: 'Click New (or Create First Assessment) to open the wizard. Step 1 — Task Details: enter an Assessment Title, choose an Assessment Type (default Generic/Routine), and optionally add Description, Location and Department. There is a Browse Workboat Tasks shortcut with 44 pre-defined WBC3 tasks. Click Next.',
      image: 'risk-02-create-form.png',
    },
    {
      text: 'Step 2 — Hazards: click Add Hazard, fill Hazard Description and Who Could Be Affected, and add more hazards if needed. Click Next.',
      image: 'risk-03-step2-hazards.png',
    },
    {
      text: 'Step 3 — Risk Score: for each hazard, click a cell in the 5×5 matrix (Likelihood × Severity) to set the Initial Risk — the score is out of 25 with a band (Low 1–5 / Medium 6–11 / High 12–19 / Extreme 20–25). Click Next.',
      image: 'risk-04-step3-riskscore.png',
    },
    {
      text: 'Step 4 — Controls: enter Existing Controls, pick a Primary Control Type from the ISO 45001 hierarchy (Elimination → … → PPE), add Additional Controls Required, then set the Residual Risk (Likelihood & Severity again) — the app shows the residual score and Risk Reduction %. Click Next.',
      image: 'risk-05-step4-controls.png',
    },
    {
      text: 'Step 5 — Review: check the summary, set the Review Frequency (default Annually), confirm the green Pre-Submission Checklist, and click Save Assessment (status Active). Or use Save & Exit on any step to save partway as a Draft.',
      image: 'risk-06-step5-review.png',
    },
    {
      text: 'Back on the list, your assessment appears as a row (Assessment · Status · Hazards · Risk Level · Review Due · Actions).',
      image: 'risk-07b-after-proper-save.png',
    },
    {
      text: 'Click the assessment name to open the formatted RISK ASSESSMENT REPORT — sections 1.0 Assessment Details, 2.0 Risk Summary, 3.0 Hazard Register, 4.0 Regulatory Compliance, 5.0 Signatures (Assessed By / Approved By, each with a Sign button). Header actions: View History · Download PDF · Edit · Delete. Tip: trust the per-hazard figures in the 3.0 Hazard Register for the authoritative risk scores.',
      image: 'risk-08-open-saved.png',
    },
  ],
};

// ─── 8. HOURS OF REST ────────────────────────────────────────────────────────

export const hoursOfRest = {
  id: 'hours-of-rest',
  title: '8. Hours of Rest',
  summary: 'Record crew work and rest hours and check them against UK MGN 505 rest limits.',
  keywords: ['hours of rest', 'hor', 'rest', 'work', 'mgn 505', 'compliance', 'violation', 'breach', 'export', 'report', 'bulk', 'record hours', 'calendar'],
  steps: [
    {
      text: 'Hours of Rest records each crew member\'s daily work and rest hours and checks them against UK MGN 505 rest limits (min 10h rest per 24h; min 77h per 7 days; rest in max 2 periods, one at least 6h), so you can prove crew are getting legally required rest. Records can be exported monthly with signature fields for the master and crew to endorse. It lives under left menu → Crew → Hours of Rest tab.',
      image: 'hor-02-landing.png',
    },
    {
      text: 'On the Hours of Rest tab you see one card per crew member (7-day rest, Last 24h, "July: N/20 days recorded • Unsigned", and a status badge), plus Hours of Rest Compliance and Quick Actions panels (Record Hours · Export Monthly Report · Bulk Hours Entry · Compliance Guide).',
      image: null,
    },
    {
      text: 'Click Record Hours. In Select Dates, click one or more days on the calendar; in Select Crew Members, click crew rows (or Select All). Save Hours stays disabled until at least one crew member AND one date are selected.',
      image: 'hor-03-record-open.png',
    },
    {
      text: 'Under Work Periods, pick a Quick Pattern (e.g. 12-hour day shift 06:00–18:00) or set the Work Times yourself; + Add Period adds a second period for split days. The Work Hours and Rest Hours tiles recalculate automatically.',
      image: 'hor-05-pattern-12h.png',
    },
    {
      text: 'Click Save Hours. The crew cards update to the recorded hours and a green Compliant badge, and "N/20 days recorded" increments. Tip: enter your times and click Save Hours before closing the panel — don\'t close it without saving, or the hours you just entered won\'t be recorded.',
      image: 'hor-07-after-save.png',
    },
    {
      text: 'How a breach shows: if you enter a long day (e.g. 06:00–23:00 = 17h work), an amber warning appears and the save button changes to Save with Exception… — so you either fix it or knowingly save it as a documented exception.',
      image: 'hor-06-violation.png',
    },
    {
      text: 'Compliance Guide (Quick Actions) opens the MGN 505 rules in plain English.',
      image: 'hor-08-guide.png',
    },
    {
      text: 'Export Monthly Report opens a modal to pick crew, month, and format — PDF (for printing/signing, includes signature fields) or JSON (for backup) — then Export.',
      image: 'hor-10-export.png',
    },
  ],
};
